import { ServiceLayout } from "@/components/ServiceLayout"
import { iotEdgeFAQs } from "@/data/faqData"

const IoTEdge = () => {
  return (
    <ServiceLayout
      title="IoT & Edge Computing"
      description="Deploying smart sensors and edge-computing networks that process data locally for millisecond-latency decision making."
      heroImage="https://images.unsplash.com/photo-1553413077-190dd305871c?auto=format&fit=crop&q=80&w=2600"
      faqs={iotEdgeFAQs}
      faqTitle="IoT & Edge Computing FAQs"
      faqSubtitle="Common questions about IoT solutions and edge computing architectures"
      features={[
        {
          title: "Digital Twin Implementation",
          description:
            "Creating virtual replicas of physical assets to simulate scenarios and predict failures before they occur.",
        },
        {
          title: "Edge Analytics",
          description:
            "Filtering and analyzing high-volume sensor data at the source, sending only relevant insights to the cloud to save bandwidth.",
        },
        {
          title: "Mesh Networking",
          description:
            "Robust device-to-device communication protocols that maintain network integrity even in disconnected environments.",
        },
      ]}
      approach={{
        title: "Fog Computing Framework",
        description:
          "Moving the 'Brain' closer to the 'Hand' for instant reflexes.",
        image:
          "https://images.unsplash.com/photo-1558346490-a72e53ae2d4f?auto=format&fit=crop&q=80&w=2600",
        content: [
          "We address the challenge of 'Data Gravity' - the fact that moving massive amounts of sensor data to the cloud is slow and expensive. Our 'Fog Computing' strategy pushes intelligence down to the edge gateway (e.g., NVIDIA Jetson or Raspberry Pi), allowing decisions to be made in microseconds.",
          "We deploy Digital Twins - virtual replicas of your physical assets. By simulating operational changes on the digital twin first, we can predict failures and optimize performance without risking physical equipment.",
          "Our secure mesh networking protocols allow devices to communicate peer-to-peer, forming a resilient swarm that functions autonomously even if the central internet connection is severed.",
        ],
      }}
      benefits={[
        "Predictive Maintenance",
        "Real-Time Asset Tracking",
        "Energy Consumption Optimization",
        "Remote Diagnostics",
        "Industrial Automation (Industry 4.0)",
        "Secure Device Management",
      ]}
      process={{
        steps: [
          {
            title: "Device Provisioning",
            description:
              "Securely deploying and authenticating sensors and gateways across your physical infrastructure using zero-touch provisioning.",
          },
          {
            title: "Edge Logic Deployment",
            description:
              "Pushing containerized analytics models (Docker) to edge devices to enable local processing and filtering.",
          },
          {
            title: "Cloud Sync Strategy",
            description:
              "Configuring efficient data transmission protocols (MQTT/CoAP) to send only anomalous or aggregated data to the cloud.",
          },
          {
            title: "Digital Twin Mapping",
            description:
              "Visualizing live device states in a 3D dashboard to monitor performance and simulate operational changes.",
          },
        ],
      }}
      techStack={[
        {
          category: "Edge Runtime",
          items: ["AWS Greengrass", "Azure IoT Edge", "Docker", "K3s"],
        },
        {
          category: "Protocols",
          items: ["MQTT", "OPC-UA", "CoAP", "LoRaWAN"],
        },
        {
          category: "Hardware Support",
          items: ["Raspberry Pi", "NVIDIA Jetson", "ESP32", "Siemens IoT"],
        },
      ]}
    />
  )
}

export default IoTEdge
